import * as React from 'react';
import { css } from 'aphrodite';
import { Modal } from '../../../../../web/components/Modal';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Input } from '@HoudiniUI/Input';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { CancelIcon } from '../../../../../web/components/svgs/icons/CancelIcon';
import {
	useTemplateBulkCopyMutation,
	type BulkTemplateCopyResponse,
	type TemplateCopyResult,
} from '../../../../../queries/Admin/Template';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { styleSheet } from './styles';
import { baseStyleSheet } from '../../../../../web/styles/styles';

interface IProps {
	isOpen: boolean;
	onRequestClose: () => void;
	destinationAccountId: string;
	accountName?: string;
}

export const ImportTemplatesModal: React.FC<IProps> = ({
	isOpen,
	onRequestClose,
	destinationAccountId,
	accountName,
}) => {
	const [templateReferences, setTemplateReferences] = React.useState('');
	const [additionalKeywords, setAdditionalKeywords] = React.useState('');
	const [results, setResults] = React.useState<BulkTemplateCopyResponse | null>(null);
	const [validationError, setValidationError] = React.useState<string | null>(null);
	const errorMessages = useErrorMessages();

	const bulkCopyMutation = useTemplateBulkCopyMutation({
		onSuccess: data => {
			setResults(data);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Validate template reference format - supports both accountId/templateId and accountId/blog/templateId
	const validateTemplateRef = (ref: string): boolean => {
		// Match accountId/templateId or accountId/blog/templateId format
		return /^[a-zA-Z0-9-]+\/(blog\/)?[a-zA-Z0-9-]+$/.test(ref);
	};

	const parseTemplateReferences = React.useMemo(() => {
		return templateReferences
			.split('\n')
			.map(line => line.trim())
			.filter(line => line.length > 0);
	}, [templateReferences]);

	const handleImport = () => {
		setValidationError(null);

		// Parse and validate template references
		const refs = parseTemplateReferences;

		if (refs.length === 0) {
			setValidationError('Please enter at least one template reference');
			return;
		}

		// Validate all references
		const invalidRefs = refs.filter(ref => !validateTemplateRef(ref));
		if (invalidRefs.length > 0) {
			setValidationError(
				`Invalid template reference format: ${invalidRefs[0]}\n` +
					'Expected formats: accountId/templateId or accountId/blog/templateId'
			);
			return;
		}

		// Parse keywords if provided
		const keywords = additionalKeywords
			.split(',')
			.map(k => k.trim())
			.filter(k => k.length > 0);

		bulkCopyMutation.mutate({
			destinationAccountId,
			templateReferences: refs,
			...(keywords.length > 0 && { additionalKeywords: keywords }),
		});
	};

	const handleClose = () => {
		setTemplateReferences('');
		setAdditionalKeywords('');
		setResults(null);
		setValidationError(null);
		bulkCopyMutation.reset();
		onRequestClose();
	};

	const getResultsSummary = () => {
		if (!results) return null;

		const succeeded = results.results.filter(r => r.success).length;
		const failed = results.results.filter(r => !r.success).length;
		const total = results.results.length;

		return {
			succeeded,
			failed,
			total,
			successRate: total > 0 ? ((succeeded / total) * 100).toFixed(1) : '0',
		};
	};

	const renderContent = () => {
		if (bulkCopyMutation.isPending) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
					<p className={css(styleSheet.loadingText)}>Importing templates...</p>
				</div>
			);
		}

		if (results) {
			const summary = getResultsSummary()!;
			return (
				<div className={css(styleSheet.resultsContainer)}>
					<div className={css(styleSheet.summarySection)}>
						<h3 className={css(styleSheet.summaryTitle)}>Import Results</h3>
						<div className={css(styleSheet.summaryStats)}>
							<div className={css(styleSheet.statItem, styleSheet.statSuccess)}>
								<span className={css(styleSheet.successIcon)}>✓</span>
								<span>
									Succeeded: {summary.succeeded}/{summary.total} ({summary.successRate}%)
								</span>
							</div>
							{summary.failed > 0 && (
								<div className={css(styleSheet.statItem, styleSheet.statError)}>
									<CancelIcon className={css(styleSheet.statIcon)} />
									<span>Failed: {summary.failed}</span>
								</div>
							)}
						</div>
					</div>

					{results.results.length > 0 && (
						<div className={css(styleSheet.detailsSection)}>
							<h4 className={css(styleSheet.detailsTitle)}>Details:</h4>
							<div className={css(styleSheet.resultsTable)}>
								{results.results.map((result: TemplateCopyResult, index: number) => (
									<div
										key={index}
										className={css(
											styleSheet.resultRow,
											result.success ? styleSheet.resultSuccess : styleSheet.resultError
										)}
									>
										<div className={css(styleSheet.resultIcon)}>
											{result.success ? (
												<span className={css(styleSheet.successCheckmark)}>✓</span>
											) : (
												<CancelIcon className={css(styleSheet.resultIconError)} />
											)}
										</div>
										<div className={css(styleSheet.resultDetails)}>
											<div className={css(styleSheet.resultName)}>{result.templateName || 'Unknown Template'}</div>
											<div className={css(styleSheet.resultReference)}>
												{result.sourceAccountId}/{result.sourceTemplateId}
											</div>
											{result.error && <div className={css(styleSheet.resultError)}>Error: {result.error}</div>}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					<div className={css(styleSheet.modalFooter)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={handleClose}>
							Close
						</button>
					</div>
				</div>
			);
		}

		return (
			<>
				<div className={css(styleSheet.warningSection)}>
					<WarningIcon className={css(styleSheet.warningIcon)} />
					<div className={css(styleSheet.warningText)}>
						<strong>Content Team Tool Only</strong>
						<p>
							This tool is designed for the Content Team to bulk import templates. Please ensure you have the proper
							authorization before proceeding.
						</p>
					</div>
				</div>

				<div className={css(styleSheet.instructionsSection)}>
					<h3 className={css(styleSheet.instructionsTitle)}>Instructions:</h3>
					<ol className={css(styleSheet.instructionsList)}>
						<li>Paste template references below, one per line</li>
						<li>
							Each reference should be in one of these formats:
							<ul>
								<li>accountId/templateId</li>
								<li>accountId/blog/templateId</li>
							</ul>
						</li>
						<li>These references are obtained from the template export CSV</li>
						<li>Click &apos;Import Templates&apos; to begin the import process</li>
					</ol>
				</div>

				<div className={css(styleSheet.inputSection)}>
					<label className={css(styleSheet.inputLabel)}>
						Template References:
						<textarea
							className={css(styleSheet.textarea, validationError && styleSheet.textareaError)}
							value={templateReferences}
							onChange={e => {
								setTemplateReferences(e.target.value);
								setValidationError(null);
							}}
							placeholder='account-123/template-456&#10;account-789/blog/template-012&#10;...'
							rows={10}
						/>
					</label>
					{validationError && (
						<div className={css(styleSheet.errorMessage)}>
							<WarningIcon className={css(styleSheet.errorIcon)} />
							<span>{validationError}</span>
						</div>
					)}
				</div>

				<div className={css(styleSheet.keywordsSection)}>
					<div className={css(styleSheet.inputLabel)}>Additional Keywords (Optional):</div>
					<Input
						type='text'
						styles={[styleSheet.keywordsInput]}
						value={additionalKeywords}
						onChange={e => setAdditionalKeywords(e.target.value)}
						placeholder='Keywords to add to all imported templates (separated by comma)'
						id='template-import-keywords'
					/>
					<div className={css(styleSheet.keywordsHelp)}>
						These keywords will be added to all imported templates. Leave blank to keep original keywords only.
					</div>
				</div>

				<div className={css(styleSheet.modalFooter)}>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleClose}>
						Cancel
					</button>
					<button
						className={css(baseStyleSheet.ctaButton)}
						onClick={handleImport}
						disabled={!templateReferences.trim()}
					>
						Import Templates
					</button>
				</div>
			</>
		);
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(styleSheet.modalTitle)}>Import Templates {accountName && `to ${accountName}`}</div>
			<div className={css(styleSheet.modalBody)}>{renderContent()}</div>
		</Modal>
	);
};
